import { css } from '@emotion/react';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useMemo, useState } from 'react';

import Box from '~/common/components/Box';
import { TOP_NAV_HEIGHT } from '~/common/components/GlobalLayout/PageLayout/Styled';
import Link from '~/common/components/Link';
import Menu from '~/common/components/Menus/Menu';
import MenuButton from '~/common/components/Menus/MenuButton';
import MenuList from '~/common/components/Menus/MenuList';
import { ImpersonateMenu } from '~/common/components/Nav/components/ImpersonateMenu';
import NavLogoLoanSummary from '~/common/components/Nav/components/NavLogoLoanSummary';
import { useUser } from '~/common/hooks/useAuthentication';
import type { LoanSummary } from '~/common/hooks/useGetLoanSummary';
import useUrl from '~/common/hooks/useUrl';
import { EASE_NAV_LINKS } from '~/pages/Home/constants';

import { StyledSection } from './Styled';

type SelectedCategoryNavContainerProps = {
  children?: React.ReactNode;
};

const SelectedCategoryNavContainer = memo(
  function SelectedCategoryNavContainer({
    children,
  }: Readonly<SelectedCategoryNavContainerProps>) {
    /**INFO: nav container for top selected categories  i.e Classify, Borrower, Liabilities, etc.*/
    const loanSummary: LoanSummary = useMemo(() => {
      const loanSummaryItem = sessionStorage.getItem('loanSummary');
      return loanSummaryItem ? JSON.parse(loanSummaryItem) : null;
    }, []);

    const hasLoanSummaryData = !!loanSummary;
    const { isReadOnly } = useUrl();

    return (
      <StyledSection isReadOnlyUrl={isReadOnly}>
        {isReadOnly && hasLoanSummaryData ? (
          <SelectedCategoryNav>{children}</SelectedCategoryNav>
        ) : (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <SelectedCategoryNav>{children}</SelectedCategoryNav>
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <BackToEaseMenu />
              <ImpersonateMenu />
            </Box>
          </Box>
        )}
      </StyledSection>
    );
  },
);

const SelectedCategoryNav = ({ children }: { children?: React.ReactNode }) => {
  const { isReadOnly } = useUrl();

  return (
    <>
      {isReadOnly && <NavLogoLoanSummary />}
      <nav aria-label="selected category navigation">
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            listStyleType: 'none',
            paddingTop: '1.25rem',
            height: TOP_NAV_HEIGHT,
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
          aria-label="category tablist"
          role="tablist"
        >
          {children}
        </div>
      </nav>
    </>
  );
};

const BackToEaseMenu = () => {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isUnderwriter } = useUser();
  const [isActive, setIsActive] = useState(false);
  const handleToggleMenu = () => {
    setIsActive(!isActive);
  };
  const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
    obfuscatedLoanIdentifier || {};

  const easeBaseUrl = `${globalThis.globalConfig.ease.baseUrl}/Loan/${encodeURIComponent(obfuscatedLenderDatabaseId)}/${encodeURIComponent(obfuscatedLoanRecordId)}`;
  const pipelineUrl = `${globalThis.globalConfig.ease.baseUrl}/PipeLine`;

  if (isUnderwriter) return null;

  return (
    <Menu onClose={handleToggleMenu} display="inline">
      <MenuButton
        color="blue.600"
        fontWeight="500"
        onClick={handleToggleMenu}
        aria-label="back to ease"
        lineHeight="1.5rem"
        display="flex"
        alignItems="center"
      >
        <Box display="flex" alignItems="center">
          EASE
          {isActive ? (
            <FontAwesomeIcon
              data-testid="fa-chevron-up-is-active"
              icon={faCaretUp}
              css={css`
                margin-left: 0.4375rem;
                margin-right: 0.4375rem;
                width: 0.4168rem;
              `}
            />
          ) : (
            <FontAwesomeIcon
              data-testid="fa-chevron-down-is-active"
              icon={faCaretDown}
              css={css`
                margin-left: 0.4375rem;
                margin-right: 0.4375rem;
                width: 0.4168rem;
              `}
            />
          )}
        </Box>
      </MenuButton>
      <MenuList paddingLeft="0.5rem" paddingRight="0.5rem" zIndex={101}>
        {EASE_NAV_LINKS.map(({ linkName, link }) => (
          <Link
            px="0.75rem"
            py="0.375rem"
            display="flex"
            href={easeBaseUrl + link}
            key={link}
            _hover={{
              textDecoration: 'none',
              background: 'gray.200',
              borderRadius: '0.4375rem',
              border: 'none',
            }}
            _focus={{
              boxShadow: 'none',
            }}
          >
            {linkName}
          </Link>
        ))}
        <Link
          px="0.75rem"
          py="0.375rem"
          display="flex"
          as={Link}
          href={pipelineUrl}
          _hover={{
            textDecoration: 'none',
            background: 'gray.200',
            borderRadius: '0.4375rem',
            border: 'none',
          }}
          _focus={{
            boxShadow: 'none',
          }}
          aria-label="your pipeline"
        >
          Your Pipeline
        </Link>
      </MenuList>
    </Menu>
  );
};

export default SelectedCategoryNavContainer;
